import { Typography, Box } from "@mui/material";
import { useEffect, useState } from "react";
import moment from "moment-timezone";

interface SessionCountdownProps {
  startTime: string;
  timeZone?: string;
  variant?: "body1" | "body2" | "caption";
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isStarted: boolean;
}

const calculateTimeRemaining = (startTime: string): TimeRemaining => {
  const now = moment();
  const start = moment(startTime);
  const diff = start.diff(now);

  if (diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isStarted: true,
    };
  }

  const duration = moment.duration(diff);

  return {
    days: Math.floor(duration.asDays()),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    isStarted: false,
  };
};

const formatTimeUnit = (value: number): string => {
  return value.toString().padStart(2, "0");
};

export const SessionCountdown = ({
  startTime,
  timeZone,
  variant = "body2",
}: SessionCountdownProps) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(
    calculateTimeRemaining(startTime),
  );

  useEffect(() => {
    // Update countdown every second
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(startTime));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  if (timeRemaining.isStarted) {
    return (
      <Typography
        variant={variant}
        color="text.secondary"
        sx={{ lineHeight: 1, margin: 0 }}
      >
        Session Started
      </Typography>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={0.5}
      sx={{
        lineHeight: 1,
        margin: 0,
        padding: 0,
      }}
    >
      <Typography
        variant={variant}
        color="primary"
        fontWeight="medium"
        sx={{ lineHeight: 1, margin: 0, whiteSpace: "nowrap" }}
      >
        Starts in:
      </Typography>
      <Typography
        variant={variant}
        fontWeight="bold"
        component="span"
        sx={{ lineHeight: 1, margin: 0, whiteSpace: "nowrap" }}
      >
        {timeRemaining.days > 0 && `${timeRemaining.days}d `}
        {formatTimeUnit(timeRemaining.hours)}h:
        {formatTimeUnit(timeRemaining.minutes)}m:
        {formatTimeUnit(timeRemaining.seconds)}s
      </Typography>
    </Box>
  );
};
